import React, { useState, useRef } from 'react';
import styles from './MandateDropdown.module.scss';
import { BLACK_DROPDOWN_CARET, PURPLE_CROSS, GREY_DROPDOWN_CARET } from '../../../../assets/hostedassets';
import { StatusDropdownOption } from '../../../../common/_custom/StatusDropdown/StatusDropdown';
import clsx from 'clsx';
import { isEmpty, truncateByCharacters } from '../../../../utils/utils';
import { useOutsideClick } from '../../../../utils/constants/hooks';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../../utils/constants/commonConstants';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import { Mixpanel } from '../../../../utils/mixpanel';
import { useAppSelector } from '../../../../app/hooks';

interface MandateDropdownProps {
    data?: Array<{
        id: string;
        dealTerms: {
            maximumDealAmount?: number | null;
            loanTypes?: string[] | null;
        };
        dealStatus: string;
        createdAt: number;
    }>;
    dealStatusConfig?: {
        mandate: StatusDropdownOption[];
    } | null;
    selectedMandate?: any;
    onSelect?: (mandate: any) => void;
    onClear?: () => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

const DEFAULT_STATUS_CONFIG = {
    text: 'Mandate',
    backgroundColor: '#f3f4f6',
    labelColor: '#374151',
} as const;

/**
 * Formats amount value with currency suffix
 * @param amount - The amount to format
 * @returns Formatted amount string or '-' for invalid values
 */
const formatAmount = (amount?: number | null): string => {
    if (isEmpty(amount) || amount === null || amount === undefined) {
        return '-';
    }
    return `${amount} Cr`;
};

/**
 * Formats timestamp to readable date string
 * @param timestamp - Unix timestamp to format
 * @returns Formatted date string or '-' for invalid timestamps
 */
const formatDate = (timestamp?: number | null): string => {
    if (!timestamp || !dayjs(timestamp).isValid()) {
        return '-';
    }
    return dayjs(timestamp).format(DATE_FORMAT);
};

/**
 * Formats product list with truncation logic
 * @param products - Array of product strings
 * @returns Formatted product string with truncation
 */
const formatProducts = (products?: string[] | null): string => {
    if (isEmpty(products) || !Array.isArray(products)) {
        return '';
    }

    if (products.length <= 2) {
        return products.join(', ');
    }

    const firstTwo = products.slice(0, 2);
    const remainingCount = products.length - 2;
    const firstProduct = firstTwo[0];
    const secondProduct = truncateByCharacters(firstTwo[1], 10);
    return `${firstProduct}, ${secondProduct} + ${remainingCount}`;
};

/**
 * Gets status configuration for a given status
 * @param status - Status string to get config for
 * @param dealStatusConfig - Configuration object containing status options
 * @returns Status configuration object
 */
const getStatusConfig = (status?: string, dealStatusConfig?: { mandate: StatusDropdownOption[] } | null) => {
    if (!status || !dealStatusConfig?.mandate || !Array.isArray(dealStatusConfig.mandate)) {
        return DEFAULT_STATUS_CONFIG;
    }

    const statusObj = dealStatusConfig.mandate.find((item) => item.value === status);

    if (statusObj) {
        return {
            text: statusObj.label || status,
            backgroundColor: statusObj.backgroundColor || DEFAULT_STATUS_CONFIG.backgroundColor,
            labelColor: statusObj.labelColor || DEFAULT_STATUS_CONFIG.labelColor,
        };
    }

    return {
        ...DEFAULT_STATUS_CONFIG,
        text: status,
    };
};

/**
 * Generates display text for selected mandate
 * @param mandate - Selected mandate object
 * @param placeholder - Placeholder text when no mandate selected
 * @returns Display text string
 */
const getDisplayText = (mandate?: any, placeholder: string = 'Select Mandate'): string => {
    if (!mandate) {
        return placeholder;
    }

    const hasAmount = !isEmpty(mandate.dealTerms?.maximumDealAmount);
    const date = formatDate(mandate.createdAt);

    if (hasAmount) {
        const amount = formatAmount(mandate.dealTerms.maximumDealAmount);
        return `Mandate for ${amount} : ${date}`;
    }

    return `Mandate : ${date}`;
};

interface MandateRowProps {
    mandate: any;
    statusConfig: any;
    onSelect: (mandate: any) => void;
    isFirst?: boolean;
    isLast?: boolean;
    isOnly?: boolean;
}

const MandateRow: React.FC<MandateRowProps> = ({ mandate, statusConfig, onSelect, isFirst, isLast, isOnly }) => {
    const handleClick = () => {
        onSelect(mandate);
    };

    const hasAmount = !isEmpty(mandate?.dealTerms?.maximumDealAmount);
    const amount = formatAmount(mandate?.dealTerms?.maximumDealAmount);
    const date = formatDate(mandate?.createdAt);
    const products = formatProducts(mandate?.dealTerms?.loanTypes);

    // Get full product list for tooltip
    const fullProducts = mandate?.dealTerms?.loanTypes?.join(', ') || '';

    return (
        <Tooltip content={fullProducts} direction="top" fitContent={true} style={{ maxWidth: '300px' }}>
            <div
                className={clsx(styles.MandateRow, {
                    [styles.FirstRow]: isFirst,
                    [styles.LastRow]: isLast,
                    [styles.OnlyRow]: isOnly,
                })}
                onClick={handleClick}
            >
                <div className={styles.MandateContent}>
                    <div className={styles.MandateTitle}>
                        {hasAmount ? (
                            <>
                                Mandate for <span className={styles.Amount}>{amount}</span> : {date}
                            </>
                        ) : (
                            `Mandate : ${date}`
                        )}
                    </div>
                    <div className={styles.MandateSubtitle}>{products}</div>
                </div>
                <div
                    className={styles.StatusTag}
                    style={{
                        backgroundColor: statusConfig?.backgroundColor || DEFAULT_STATUS_CONFIG.backgroundColor,
                        color: statusConfig?.labelColor || DEFAULT_STATUS_CONFIG.labelColor,
                    }}
                >
                    {truncateByCharacters(statusConfig?.text || '', 12)}
                </div>
            </div>
        </Tooltip>
    );
};

interface DropdownTriggerProps {
    displayText: string;
    isOpen: boolean;
    disabled: boolean;
    hasSelection: boolean;
    onToggle: () => void;
    onClear: (e: React.MouseEvent) => void;
    email: string;
}

const DropdownTrigger: React.FC<DropdownTriggerProps> = ({ displayText, isOpen, disabled, hasSelection, onToggle, onClear, email }) => {
    const handleClick = () => {
        Mixpanel.track('Select Mandate', {
            currentUrl: window.location.href,
            userEmail: email,
        });
        if (!disabled) {
            onToggle();
        }
    };

    return (
        <div
            className={clsx(styles.DropdownTrigger, {
                [styles.NoSelectedMandate]: !hasSelection,
                [styles.Disabled]: disabled,
            })}
            onClick={handleClick}
        >
            <span className={styles.TriggerText}>{displayText}</span>
            <div className={styles.IconContainer}>
                {hasSelection && !disabled && <img src={PURPLE_CROSS} alt="clear" className={styles.ClearIcon} onClick={onClear} />}
                {!hasSelection && (
                    <img
                        src={hasSelection ? BLACK_DROPDOWN_CARET : GREY_DROPDOWN_CARET}
                        alt="dropdown"
                        className={clsx(styles.DropdownIcon, {
                            [styles.DropdownIconOpen]: isOpen,
                        })}
                    />
                )}
            </div>
        </div>
    );
};

const MandateDropdown: React.FC<MandateDropdownProps> = ({
    data = [],
    dealStatusConfig,
    selectedMandate,
    onSelect,
    onClear,
    placeholder = 'Select Mandate',
    className = '',
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const user = useAppSelector((state) => state.user);

    useOutsideClick(dropdownRef, () => setIsOpen(false));

    const handleMandateSelect = (mandate: any) => {
        setIsOpen(false);
        onSelect?.(mandate);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClear?.();
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const displayText = getDisplayText(selectedMandate, placeholder);
    const hasValidData = Array.isArray(data) && data.length > 0;

    return (
        <div className={clsx(styles.MandateDropdown, className)} ref={dropdownRef}>
            <DropdownTrigger
                displayText={displayText}
                isOpen={isOpen}
                disabled={disabled}
                hasSelection={!!selectedMandate}
                onToggle={toggleDropdown}
                onClear={handleClear}
                email={user?.userData?.primaryEmail}
            />

            <div
                className={clsx(styles.DropdownMenu, {
                    [styles.Open]: isOpen,
                })}
            >
                <div className={styles.DropdownContent}>
                    {!hasValidData ? (
                        <div className={styles.EmptyState}>No mandates available</div>
                    ) : (
                        data.map((mandate, index) => {
                            const statusConfig = getStatusConfig(mandate.dealStatus, dealStatusConfig);
                            const isFirst = index === 0;
                            const isLast = index === data.length - 1;
                            const isOnly = data.length === 1;
                            return (
                                <MandateRow
                                    key={mandate.id}
                                    mandate={mandate}
                                    statusConfig={statusConfig}
                                    onSelect={handleMandateSelect}
                                    isFirst={isFirst}
                                    isLast={isLast}
                                    isOnly={isOnly}
                                />
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default MandateDropdown;
